import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import moment from 'moment';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { CustomerMessageDownload } from '@codefluegel/wowis-rest-typescript-client';
import { useCallback, useState } from 'react';
import useMedia from '../hooks/useMedia';
import GridContainer from '../components/container/GridContainer';
import theme from '../theme';
import { useMessagesApi } from '../ApiClient';
import MessageModal from '../components/dialog/MessageModal';
import { useStore } from '../store/context';

const DamageReport = observer((): JSX.Element => {
  const messageApi = useMessagesApi();
  const [modalState, setModalState] = useState(false);
  const [currentMessage, setCurrentMessage] =
    useState<CustomerMessageDownload | null>(null);
  const isMobile = useMedia('md');
  const { messageStore } = useStore();

  const markRead = useCallback(
    async (message: CustomerMessageDownload) => {
      if (!message.messageId) {
        return;
      }

      try {
        const { data: messageResponse } =
          await messageApi.markCustomerMessageRead(message.messageId);

        if (!messageResponse.isRead) {
          return;
        }

        messageStore.updateMessageReadStatus(
          message.messageId,
          messageResponse.isRead,
        );
      } catch (e) {
        console.log(e);
      }
    },
    [messageApi, messageStore],
  );

  return (
    <Box width="100%">
      <GridContainer>
        <Grid
          container
          spacing={1}
          sx={{ paddingLeft: isMobile ? 1 : 8, paddingRight: isMobile ? 1 : 0 }}
        >
          <Grid item xs={12} md={12} p={{ xs: 0, md: 2 }}>
            <Typography
              color="secondary"
              sx={{ marginBottom: 2, marginLeft: isMobile ? 1 : 0 }}
              variant="h4"
            >
              Mitteilungen
            </Typography>
            <Box display="flex" flexDirection="row" flexWrap="wrap">
              {messageStore.messages &&
                messageStore.messages.map(message => (
                  <Card
                    sx={{
                      width: '450px',
                      margin: 1,
                      backgroundColor: theme.palette.secondary.light,
                    }}
                  >
                    <Box
                      key={message.messageId}
                      m={0}
                      sx={{
                        height: 'auto',
                        padding: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="h5" color="secondary">
                          {message.title}
                        </Typography>
                        {!message.isRead && (
                          <Tooltip title="Als gelesen markieren">
                            <IconButton
                              sx={{
                                color: theme.palette.primary.main,
                                border: `1px solid ${theme.palette.primary.main}`,
                                borderRadius: '50%',
                              }}
                              onClick={() => markRead(message)}
                            >
                              <FiberNewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        color="secondary"
                        variant="inherit"
                        sx={{
                          fontSize: 12,
                          fontWeight: 'light',
                          lineHeight: '1.5rem',
                        }}
                      >
                        {moment(message.createdAt).format('DD.MM.YYYY')}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          sx={{ fontSize: 12 }}
                          variant="outlined"
                          onClick={() => {
                            if (message) setCurrentMessage(message);
                            setModalState(true);
                          }}
                        >
                          Details
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                ))}
            </Box>
          </Grid>
        </Grid>
      </GridContainer>
      {modalState && (
        <MessageModal
          onClose={() => {
            setModalState(false);
            if (currentMessage) markRead(currentMessage);
          }}
          open={modalState}
          message={currentMessage}
        />
      )}
    </Box>
  );
});

export default DamageReport;
